import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '@/src/store'
import { ProcessingSpeed } from '@/src/types/visa/visaRequirementsType'
import {
    FareReferenceMap,
    VisaOption,
    VisaOptionsData
} from '@/src/types/visa/visaRequirementsType'
import { DeviceType } from '@/src/types'
import { AxiosResponse } from 'axios'
import { VISA_ERRORS } from '@/src/constants/error'
import { getVisaOptions } from '@/src/api/services/visaService'

interface FAQItem {
    Title: string
    Description: string
}

interface FAQSection {
    Heading: string
    Data: FAQItem[]
}

interface QuestionAndAnswersSection {
    Heading: string
    Data: FAQItem[]
}

interface VisaRequirementsState {
    isLoading: boolean
    visaType: {
        options: VisaOption[]
        selectedOption: VisaOption | null
    }
    processingSpeed: ProcessingSpeed | null
    fareRef: FareReferenceMap
    dateOfArrival: string | null
    paxDetails: {
        adults: number
        children: number
    }
    faq: FAQSection | null
    questionAndAnswers: QuestionAndAnswersSection | null
    error: string
}

const initialState: VisaRequirementsState = {
    isLoading: false,
    visaType: {
        options: [],
        selectedOption: null
    },
    processingSpeed: null,
    fareRef: {},
    dateOfArrival: null,
    paxDetails: {
        adults: 1,
        children: 0
    },
    faq: null,
    questionAndAnswers: null,
    error: ''
}

export const fetchVisaRequirements = createAsyncThunk(
    'visaRequirements/fetchVisaRequirements',
    async ({
        nationalityCode,
        destinationCountryCode,
        language,
        deviceType
    }: {
        nationalityCode: string
        destinationCountryCode: string
        language: string
        deviceType: DeviceType
    }) => {
        const payload = {
            nationalityCode,
            destinationCountryCode,
            language
        }
        const response = await getVisaOptions(payload, deviceType)
        return response as AxiosResponse<VisaOptionsData>
    }
)

const visaRequirementsSlice = createSlice({
    name: 'visaRequirements',
    initialState,
    reducers: {
        setVisaType: (state, action: { payload: VisaOption }) => {
            state.visaType.selectedOption = action.payload
        },
        setProcessingSpeed: (
            state,
            action: { payload: ProcessingSpeed | null }
        ) => {
            state.processingSpeed = action.payload
        },
        setDateOfArrival: (state, action: { payload: string }) => {
            state.dateOfArrival = action.payload
        },
        setPaxCount: (
            state,
            action: { payload: { adults: number; children: number } }
        ) => {
            state.paxDetails = {
                ...state.paxDetails,
                ...action.payload
            }
        },
        setFAQ: (state, action: { payload: FAQSection }) => {
            state.faq = action.payload
        },
        setQuestionAndAnswers: (
            state,
            action: { payload: QuestionAndAnswersSection }
        ) => {
            state.questionAndAnswers = action.payload
        },
        setError: (state, action: { payload: string }) => {
            state.error = action.payload
        },
        resetVisaRequirementsData: () => {
            return initialState
        }
    },
    extraReducers(builder) {
        builder.addCase(fetchVisaRequirements.pending, (state) => {
            state.isLoading = true
        })
        builder.addCase(fetchVisaRequirements.fulfilled, (state, action) => {
            state.isLoading = false
            const { data, status } = action.payload || {}
            if (status === 200) {
                const { visaOptions, fareRef, faq, questionAndAnswers } =
                    data || {}
                state.visaType = {
                    options: visaOptions,
                    selectedOption: null
                }
                state.fareRef = fareRef
                state.faq = faq || null
                state.questionAndAnswers = questionAndAnswers || null
            } else {
                state.error = VISA_ERRORS.FETCH_VISA_REQUIREMENTS_ERROR
            }
        })
        builder.addCase(fetchVisaRequirements.rejected, (state) => {
            state.isLoading = false
        })
    }
})

export const {
    setVisaType,
    setProcessingSpeed,
    setDateOfArrival,
    resetVisaRequirementsData,
    setPaxCount,
    setFAQ,
    setQuestionAndAnswers,
    setError
} = visaRequirementsSlice.actions

export const getVisaRequirementsState = (state: RootState) =>
    state.visaRequirements

export const getFAQData = (state: RootState) => state.visaRequirements.faq

export const getQuestionAndAnswersData = (state: RootState) =>
    state.visaRequirements.questionAndAnswers

export default visaRequirementsSlice.reducer
